import { useMemo, useState } from "react";

import {
  useLoadFields,
  useLoadGroups,
  useLoadSections,
} from "@smart/manage-gql-client-dom";

import { LanguageKey } from "../../../types";
import {
  EditableSection,
  GqlField,
  GqlForm,
  GqlGroup,
  GqlSection,
} from "../types";
import {
  buildEntitiesLookups,
  buildSectionChangeEntries,
  computeFieldChanges,
  computeGroupChanges,
  computeSectionChangeCounts,
  computeSectionChanges,
  EntityChangesMap,
  SectionChangeCounts,
  SectionChangeEntry,
} from "../utils";

const filterRemoved = <T extends { values?: { uri: string } | null }>(
  items: T[] | null | undefined,
  changesMap: EntityChangesMap,
  showChanges: boolean,
): T[] =>
  showChanges
    ? (items ?? []).filter(
        (item) =>
          item.values?.uri && changesMap.get(item.values.uri) === "removed",
      )
    : [];

type UseFormChangesInput = {
  formUri: string;
  form?: GqlForm | null;
  sections?: GqlSection[] | null;
  fields?: GqlField[] | null;
  groups?: GqlGroup[] | null;
  sectionsToUse: EditableSection[];
  viewingLanguage: LanguageKey;
};

export const useFormChanges = ({
  formUri,
  form,
  sections,
  fields,
  groups,
  sectionsToUse,
  viewingLanguage,
}: UseFormChangesInput) => {
  const [highlightChanges, setHighlightChanges] = useState(true);

  const hasUnpublishedChanges = !!form?.rawForm.values.hasUnpublishedChanges;

  const trackChanges =
    hasUnpublishedChanges && !!form?.rawForm.values.publishedFormVersionUri;

  const { result: publishedFields } = useLoadFields(
    { formUri, usePublishedVersion: true },
    { skip: !trackChanges, fetchPolicy: "no-cache" },
  );

  const { result: publishedSections } = useLoadSections(
    { formUri, usePublishedVersion: true },
    { skip: !trackChanges, fetchPolicy: "no-cache" },
  );

  const { result: publishedGroups } = useLoadGroups(
    { formUri, usePublishedVersion: true },
    { skip: !trackChanges, fetchPolicy: "no-cache" },
  );

  const fieldChangesMap: EntityChangesMap = useMemo(
    () =>
      trackChanges && publishedFields && fields
        ? computeFieldChanges(
            fields.map((f) => f.values),
            publishedFields.map((f) => f.values),
          )
        : new Map(),
    [trackChanges, publishedFields, fields],
  );

  const sectionChangesMap = useMemo(
    () =>
      trackChanges && publishedSections && sections
        ? computeSectionChanges(
            sections
              .filter((s) => s.values)
              .map(
                (s) => s.values as NonNullable<(typeof sections)[0]["values"]>,
              ),
            publishedSections.map((s) => s.values),
          )
        : new Map(),
    [trackChanges, publishedSections, sections],
  );

  const groupChangesMap = useMemo(
    () =>
      trackChanges && publishedGroups && groups
        ? computeGroupChanges(
            groups.map((g) => g.values),
            publishedGroups.map((g) => g.values),
          )
        : new Map(),
    [trackChanges, publishedGroups, groups],
  );

  const { fieldUriToSectionUri, groupUriToSectionUri } = useMemo(
    () =>
      buildEntitiesLookups({
        fields,
        publishedFields,
        groups,
        publishedGroups,
      }),
    [fields, publishedFields, groups, publishedGroups],
  );

  const sectionChangeCounts: SectionChangeCounts = useMemo(
    () =>
      computeSectionChangeCounts({
        fieldChangesMap,
        sectionChangesMap,
        groupChangesMap,
        fieldUriToSectionUri,
        groupUriToSectionUri,
      }),
    [
      fieldChangesMap,
      sectionChangesMap,
      groupChangesMap,
      fieldUriToSectionUri,
      groupUriToSectionUri,
    ],
  );

  const sectionChangeEntries: SectionChangeEntry[] = useMemo(
    () =>
      buildSectionChangeEntries({
        sectionChangeCounts,
        sectionChangesMap,
        currentSections: sectionsToUse,
        publishedSections:
          publishedSections
            ?.filter((section) => section.values)
            .map((section) => ({
              uri: section.uri,
              values: section.values,
            })) ?? [],
        viewingLanguage,
      }),
    [
      sectionChangeCounts,
      sectionChangesMap,
      sectionsToUse,
      publishedSections,
      viewingLanguage,
    ],
  );

  const effectiveFieldChangesMap: EntityChangesMap = useMemo(
    () => (highlightChanges ? fieldChangesMap : new Map()),
    [highlightChanges, fieldChangesMap],
  );

  const effectiveGroupChangesMap: EntityChangesMap = useMemo(
    () => (highlightChanges ? groupChangesMap : new Map()),
    [highlightChanges, groupChangesMap],
  );
  const effectiveSectionChangesMap: EntityChangesMap = useMemo(
    () => (highlightChanges ? sectionChangesMap : new Map()),
    [highlightChanges, sectionChangesMap],
  );

  const showRemovedChanges = trackChanges && highlightChanges;

  const removedPublishedFields = useMemo(
    () => filterRemoved(publishedFields, fieldChangesMap, showRemovedChanges),
    [showRemovedChanges, publishedFields, fieldChangesMap],
  );

  const removedPublishedGroups = useMemo(
    () => filterRemoved(publishedGroups, groupChangesMap, showRemovedChanges),
    [showRemovedChanges, publishedGroups, groupChangesMap],
  );

  const removedPublishedSections = useMemo(
    () =>
      filterRemoved(publishedSections, sectionChangesMap, showRemovedChanges),
    [showRemovedChanges, publishedSections, sectionChangesMap],
  );

  return {
    highlightChanges,
    setHighlightChanges,
    sectionChangeCounts,
    sectionChangeEntries,
    effectiveFieldChangesMap,
    effectiveGroupChangesMap,
    effectiveSectionChangesMap,
    removedPublishedFields,
    removedPublishedGroups,
    removedPublishedSections,
  };
};
